import { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FrequencyToggle } from "@/components/donate/FrequencyToggle";
import { AmountSelector } from "@/components/donate/AmountSelector";
import { DonorInfoForm } from "@/components/donate/DonorInfoForm";

export default function Donate() {
  const { user } = useAuth();
  const [frequency, setFrequency] = useState("one_time"); // one_time | monthly
  const [selectedPreset, setSelectedPreset] = useState(35);
  const [customAmount, setCustomAmount] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setName((prev) => prev || user.name || "");
      setEmail((prev) => prev || user.email || "");
    }
  }, [user]);

  const amount = useCustom ? parseFloat(customAmount) : selectedPreset;

  const handleSelectPreset = (p) => { setSelectedPreset(p); setUseCustom(false); };
  const handleCustomAmountChange = (val) => { setCustomAmount(val); setUseCustom(true); };

  const handleDonate = async () => {
    if (!amount || amount < 1) {
      setError("Please enter an amount of at least $1");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post("/donations/checkout", {
        amount, frequency, donor_name: name || null, donor_email: email || null, origin_url: originUrl,
      });
      window.location.href = data.url;
    } catch (e) {
      setError("Something went wrong starting your donation. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-10">
        <div className="inline-flex h-14 w-14 rounded-full bg-[#EAF3F7] border border-zinc-200 items-center justify-center mb-5">
          <Heart className="h-6 w-6 text-[#1E3A6E]" />
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E] mb-2">Push the Change</p>
        <h1 className="font-display font-extrabold uppercase text-3xl md:text-4xl text-zinc-950 tracking-tight mb-4">Fund the Movement</h1>
        <p className="text-zinc-600 leading-relaxed max-w-xl mx-auto">
          Your donation directly funds building out BERN (our emergency response network), advocacy and
          lobbying work, community outreach, and keeping documentation and accountability tools free for
          everyone who needs them.
        </p>
      </div>

      {error && <div className="mb-6 p-3 rounded-sm bg-red-50 border border-red-200 text-red-700 text-sm text-center">{error}</div>}

      <FrequencyToggle frequency={frequency} onChange={setFrequency} />

      <AmountSelector
        frequency={frequency}
        selectedPreset={selectedPreset}
        useCustom={useCustom}
        customAmount={customAmount}
        onSelectPreset={handleSelectPreset}
        onToggleCustom={() => setUseCustom(true)}
        onCustomAmountChange={handleCustomAmountChange}
      />

      <DonorInfoForm
        name={name}
        email={email}
        amount={amount}
        frequency={frequency}
        loading={loading}
        onNameChange={setName}
        onEmailChange={setEmail}
        onSubmit={handleDonate}
      />
    </div>
  );
}
